import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { startSetTicket, startAddReplyTicket, startCloseTicket } from '../../actions/tickets';
import { startSetUnreadTicketsCount } from '../../actions/webmaster';
import Card from '../../components/Card/Card';
import ReplyTicketForm from '../../components/Tickets/ReplyTicketForm';
import ReplyList from '../../components/Tickets/ReplyList';
import NotFoundPage from '../errors/NotFoundPage';
import WithAlert from '../../hoc/WithAlert';
import { iFMessage } from '../../hoc/IntlComponents';
import CloseTicketConfirmModal from '../../modals/CloseTicketConfirmModal';
import SVG from 'react-inlinesvg';
import { STATIC_PATH } from '../../config';

const Ticket = ({ id, showAlert, intl, toggleTicketModal }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState({});
  const [additions, setAdditions] = useState([]);
  const [error, setError] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const getTicket = () => {
    if (!isNaN(id)) {
      setLoading(true);

      dispatch(startSetTicket(id))
        .then(({ ticket: ticketData, additions: additionsData }) => {
          setTicket(ticketData);
          setAdditions(additionsData);
          setLoading(false);
        })
        .then(() => dispatch(startSetUnreadTicketsCount()))
        .catch(({ response }) => {
          if (response.status === 400) {
            setError(response.data.error.message.message);
          }
        });
    }
  };

  useEffect(() => {
    getTicket();
  }, [id]);

  const submit = (data) => {
    const formData = new FormData();
    let message = '';

    Object.keys(data).forEach((paramName) => {
      if (Array.isArray(data[paramName])) {
        for (let i in data[paramName]) {
          formData.append([paramName], data[paramName][i]);
        }
      } else {
        formData.append([paramName], data[paramName]);
      }
    });

    return dispatch(startAddReplyTicket(id, formData))
      .then(() => {
        getTicket();
        return message;
      })
      .catch(({ response = {} }) => {
        if (response.data && response.data.error) {
          const { message: error } = response.data.error;
          message = error.message;
        } else {
          message = iFMessage({ intl, id: 'main.error' });
        }
        showAlert({
          message,
          alertType: 'danger',
          closeInSeconds: 5,
        });
        return message;
      });
  };

  const closeTicket = () => {
    dispatch(startCloseTicket(ticket.id)).then(() => getTicket());
  };

  const handleCloseTicketButtonClick = () => {
    setShowConfirmationModal(true);
  };

  const handleYesModalButtonClick = () => {
    closeTicket();
    setShowConfirmationModal(false);
  };

  const handleNoModalButtonClick = () => {
    setShowConfirmationModal(false);
  };

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Card loading={loading}>
        {ticket.id && (
          <>
            <div className="ticket-title-container">
              <Button
                variant="secondary"
                className="ticket-modal-close"
                onClick={toggleTicketModal}
              >
                <SVG
                  className="ticket-modal-close-icon-desc"
                  src={`${STATIC_PATH}/img/action-icons/arrows-icon.svg`}
                  height="21"
                  width="21"
                  cacheRequests
                />
                <SVG
                  className="ticket-modal-close-icon-mob"
                  src={`${STATIC_PATH}/img/action-icons/arrow.svg`}
                  height="21"
                  width="21"
                  cacheRequests
                />
              </Button>
              <h2 className="ticket-title">
                {ticket.title ? ticket.title : `Ticket #${ticket.id}`}
              </h2>
              {ticket.isClosed && (
                <div className="badge badge-primary">
                  <FormattedMessage id="tickets.closed_ticket" defaultMessage="Request resolved" />
                </div>
              )}
              {!ticket.isClosed && (
                <Button
                  onClick={handleCloseTicketButtonClick}
                  className="btn btn-sm ticket-close-button"
                >
                  <FormattedMessage id="tickets.close_ticket" defaultMessage="Request resolved" />
                </Button>
              )}
            </div>

            {additions.length > 0 ? (
              <ReplyList replies={additions} ticketId={ticket.id} getTicket={getTicket} />
            ) : (
              <div className="card-empty">
                <FormattedMessage id="tickets.no_tickets" defaultMessage="No Tickets" />
              </div>
            )}
            {!ticket.isClosed && <ReplyTicketForm submit={submit} />}
          </>
        )}
      </Card>

      <CloseTicketConfirmModal
        isOpen={showConfirmationModal}
        onYesCkick={handleYesModalButtonClick}
        onNoClick={handleNoModalButtonClick}
      />
    </>
  );
};

export default WithAlert(injectIntl(Ticket));
